/**
 * IncidentDialog — create + view/edit incidents.
 * Tabs:
 *   1. Details — customer / service / description / location (Leaflet)
 *   2. Assignments — multi-contractor + multi-service over time (split billing per spec B2/B3)
 *   3. Attachments — upload/download/delete via GridFS
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Plus, Upload, Download, Trash2, Square, CheckCircle2, XCircle, RotateCw, FileText, ImageIcon, Paperclip, Mail, Send, Loader2, Edit, Briefcase, Calendar, Clock, Users, Receipt, Activity, MessageSquare, UserPlus, UserMinus, Timer, AlertTriangle, Wallet, Plane, Hotel, MoreHorizontal, Ban,
} from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { compressImageIfNeeded } from '../../lib/imageCompress';
import { CloseApprovalDialog } from './CloseApprovalDialog';
import { LocationPicker } from '../location/LocationPicker';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useLiveEvents } from '../../lib/events';

const STATUS_STYLE = {
  open: 'bg-red-100 text-red-700 border-red-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  pending_approval: 'bg-violet-100 text-violet-700 border-violet-200',
  closed: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-slate-200 text-slate-600 border-slate-300',
};

const STATUS_LABEL = {
  open: 'Open', in_progress: 'In Progress',
  pending_approval: 'Pending Approval',
  closed: 'Closed', cancelled: 'Cancelled',
};

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtDuration = (start, end) => {
  if (!start) return '—';
  const s = new Date(start), e = end ? new Date(end) : new Date();
  const mins = Math.max(0, Math.floor((e - s) / 60000));
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${h}h ${m}m`;
};
const fmtR = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtRelative = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 5) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const IncidentDialog = ({ open, onOpenChange, incident, customers, contractors, services, onSaved, defaultTab = 'details' }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEdit = Boolean(incident?.id);
  const isLocked = ['closed', 'cancelled'].includes(incident?.status);
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    if (open) setActiveTab(defaultTab);
  }, [open, defaultTab, incident?.id]);

  const [form, setForm] = useState({
    customer_id: '', primary_service_id: '', description: '',
    location_address: '', location_lat: null, location_lng: null, po_number: '',
    end_client_name: '',
    outcome: '',
    vehicle_make: '', vehicle_model: '', vehicle_colour: '', vehicle_type: '',
    vehicle_registration: '',
    initial_contractor_id: '', initial_service_id: '',
  });
  // Snapshot of the form values at the moment the dialog opened. On save we
  // diff `form` against `initialForm` and PATCH only the fields THIS controller
  // actually changed — so simultaneous edits by two controllers merge cleanly
  // instead of stomping each other. The controller who saves second only
  // updates their touched fields; every other field keeps the first
  // controller's freshly-saved value.
  const [initialForm, setInitialForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [actioning, setActioning] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState({ open: false, mode: 'approve' });
  const [cancelDialog, setCancelDialog] = useState(false);
  const [closeOutcome, setCloseOutcome] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [reportDialog, setReportDialog] = useState(false);
  const [reportRecipients, setReportRecipients] = useState('');
  const [reportNote, setReportNote] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [captionDrafts, setCaptionDrafts] = useState({}); // {attId: text}
  const [captionEditing, setCaptionEditing] = useState({}); // {attId: bool}
  const [uploadCaption, setUploadCaption] = useState('');
  const [newAssign, setNewAssign] = useState({ contractor_id: '', service_id: '' });
  const [billing, setBilling] = useState(null);
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [vehicleCatalogue, setVehicleCatalogue] = useState({ makes: [], colours: [], types: [] });
  // ----- Expenses (S&T / Accommodation / Other) -----
  const [newExpense, setNewExpense] = useState({
    kind: 's_and_t', description: '', amount: '', incurred_on: '',
  });
  const [expenseReceiptFile, setExpenseReceiptFile] = useState(null);
  const expenseReceiptInputRef = useRef(null);
  const [savingExpense, setSavingExpense] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    api.get('/vehicle-catalogue')
      .then((r) => {
        if (cancelled) return;
        const items = r.data || [];
        setVehicleCatalogue({
          makes: items.filter((i) => i.kind === 'make'),
          colours: items.filter((i) => i.kind === 'colour'),
          types: items.filter((i) => i.kind === 'type'),
        });
      })
      .catch(() => { /* non-blocking — dropdowns will simply be empty */ });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    api.get('/note-templates')
      .then((r) => { if (!cancelled) setNoteTemplates(r.data || []); })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || !incident?.id) { setBilling(null); return; }
    let cancelled = false;
    api.get(`/billing/incident/${incident.id}`)
      .then((r) => { if (!cancelled) setBilling(r.data); })
      .catch(() => { /* non-blocking — KPI will simply show — */ });
    return () => { cancelled = true; };
  }, [open, incident?.id, incident?.updated_at, incident?.status, (incident?.assignments || []).length]);

  useEffect(() => {
    if (open) {
      const seeded = incident ? {
        customer_id: incident.customer_id,
        primary_service_id: incident.primary_service_id,
        description: incident.description || '',
        location_address: incident.location_address || '',
        location_lat: incident.location_lat ?? null,
        location_lng: incident.location_lng ?? null,
        po_number: incident.po_number || '',
        end_client_name: incident.end_client_name || '',
        outcome: incident.outcome || '',
        vehicle_make: incident.vehicle_make || '',
        vehicle_model: incident.vehicle_model || '',
        vehicle_colour: incident.vehicle_colour || '',
        vehicle_type: incident.vehicle_type || '',
        vehicle_registration: incident.vehicle_registration || '',
      } : {
        customer_id: customers?.[0]?.id || '',
        primary_service_id: services?.[0]?.id || '',
        description: '', location_address: '', location_lat: null, location_lng: null, po_number: '', end_client_name: '', outcome: '',
        vehicle_make: '', vehicle_model: '', vehicle_colour: '', vehicle_type: '', vehicle_registration: '',
        initial_contractor_id: '', initial_service_id: '',
      };
      setForm(seeded);
      // Snapshot for the field-level merge diff at save time.
      setInitialForm(seeded);
      setCloseOutcome(''); setCancelReason('');
      setNewAssign({ contractor_id: contractors?.[0]?.id || '', service_id: incident?.primary_service_id || services?.[0]?.id || '' });
    }
  }, [open, incident, customers, contractors, services]);

  const customerMap = useMemo(() => Object.fromEntries((customers || []).map((c) => [c.id, c])), [customers]);
  const contractorMap = useMemo(() => Object.fromEntries((contractors || []).map((c) => [c.id, c])), [contractors]);
  const serviceMap = useMemo(() => Object.fromEntries((services || []).map((s) => [s.id, s])), [services]);

  const reload = useCallback(async () => {
    if (incident?.id) {
      try {
        const r = await api.get(`/incidents/${incident.id}`);
        onSaved?.(r.data);
      } catch (e) { /* ignore */ }
    }
  }, [incident?.id, onSaved]);

  // ----- Activity timeline (derived from incident object) -----
  const activity = useMemo(() => {
    if (!incident) return [];
    const entries = [];
    if (incident.opened_at) {
      entries.push({ at: incident.opened_at, icon: Activity, label: 'Incident opened', tone: 'open' });
    }
    (incident.assignments || []).forEach((a) => {
      const cName = contractorMap[a.contractor_id]?.company_name || 'Contractor';
      if (a.started_at) entries.push({ at: a.started_at, icon: UserPlus, label: `${cName} assigned`, tone: 'assign' });
      if (a.ended_at) entries.push({ at: a.ended_at, icon: UserMinus, label: `${cName} stood down`, tone: 'assign-end' });
    });
    (incident.notes || []).forEach((n) => {
      const author = n.author_name || 'Operator';
      const preview = (n.text || '').slice(0, 40) + ((n.text || '').length > 40 ? '…' : '');
      entries.push({ at: n.created_at, icon: MessageSquare, label: `${author}: ${preview}`, tone: 'note' });
      if (n.edited_at && n.edited_at !== n.created_at) {
        entries.push({ at: n.edited_at, icon: Edit, label: `Note edited by ${n.edited_by || 'admin'}`, tone: 'note-edit' });
      }
    });
    (incident.attachments || []).forEach((a) => {
      const isImg = (a.content_type || '').startsWith('image/');
      entries.push({
        at: a.uploaded_at,
        icon: isImg ? ImageIcon : Paperclip,
        label: `${isImg ? 'Photo' : 'File'} attached — ${a.filename}`,
        tone: 'attach',
      });
    });
    if (incident.close_requested_at) entries.push({ at: incident.close_requested_at, icon: Clock, label: 'Close requested — awaiting admin approval', tone: 'attach' });
    if (incident.close_approved_at) entries.push({ at: incident.close_approved_at, icon: CheckCircle2, label: 'Close approved by admin', tone: 'close' });
    if (incident.closed_at) entries.push({ at: incident.closed_at, icon: CheckCircle2, label: 'Incident closed', tone: 'close' });
    if (incident.cancelled_at) entries.push({ at: incident.cancelled_at, icon: XCircle, label: 'Incident cancelled', tone: 'cancel' });
    return entries
      .filter((e) => !!e.at)
      .sort((a, b) => new Date(b.at) - new Date(a.at))
      .slice(0, 5);
  }, [incident, contractorMap]);

  // Subscribe to live events for THIS incident — auto-refresh so the ticker stays current.
  useLiveEvents(
    [
      'incident.updated', 'incident.closed', 'incident.cancelled', 'incident.reopened',
      'incident.close_requested', 'incident.close_rejected',
      'incident.assignment_added', 'incident.assignment_ended',
      'incident.note_added', 'incident.note_updated', 'incident.note_deleted',
      'incident.attachment_added', 'incident.attachment_updated',
      'incident.expense_added', 'incident.expense_updated', 'incident.expense_deleted',
    ],
    (evt) => {
      if (!open || !incident?.id) return;
      if (evt?.payload?.id !== incident.id) return;
      reload();
    },
  );

  // Re-render the ticker + SLA every 10s so timestamps and the SLA countdown stay accurate.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => forceTick((n) => n + 1), 10000);
    return () => clearInterval(id);
  }, [open]);

  // ----- SLA computation (per primary service `response_sla_minutes`) -----
  const sla = useMemo(() => {
    if (!incident?.id) return null;
    const svc = serviceMap[incident.primary_service_id];
    const slaMin = svc?.response_sla_minutes || 0;
    if (!slaMin) return null;
    const opened = incident.opened_at ? new Date(incident.opened_at).getTime() : null;
    if (!opened) return null;
    const stopAt = ['closed', 'cancelled'].includes(incident.status)
      ? new Date(incident.closed_at || incident.cancelled_at || incident.opened_at).getTime()
      : Date.now();
    const elapsedMin = (stopAt - opened) / 60000;
    const remainingMin = slaMin - elapsedMin;
    const pct = Math.min(999, (elapsedMin / slaMin) * 100);
    let tone = 'ok';
    if (pct >= 100) tone = 'breach';
    else if (pct >= 75) tone = 'red';
    else if (pct >= 50) tone = 'amber';
    return { slaMin, elapsedMin, remainingMin, pct, tone };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.id, incident?.status, incident?.opened_at, incident?.closed_at, incident?.cancelled_at,
      incident?.primary_service_id, serviceMap, forceTick]);

  // Fire one-shot escalation toasts at 75% and 100% — only while live.
  const slaToastFiredRef = useRef({});
  useEffect(() => {
    if (!sla || !incident?.id) return;
    if (!['open', 'in_progress'].includes(incident.status)) return;
    const fired = slaToastFiredRef.current[incident.id] || {};
    if (sla.pct >= 75 && !fired.warn) {
      toast.warning(
        `SLA threshold crossed (75%) — ${serviceMap[incident.primary_service_id]?.name || 'incident'} response`,
        { description: `${incident.incident_no} · ${Math.max(0, Math.ceil(sla.remainingMin))} min remaining`, duration: 8000 },
      );
      slaToastFiredRef.current[incident.id] = { ...fired, warn: true };
    }
    if (sla.pct >= 100 && !fired.breach) {
      toast.error(
        `SLA BREACHED — ${serviceMap[incident.primary_service_id]?.name || 'incident'} response`,
        { description: `${incident.incident_no} · over by ${Math.max(0, Math.floor(-sla.remainingMin))} min`, duration: 12000 },
      );
      slaToastFiredRef.current[incident.id] = { ...slaToastFiredRef.current[incident.id], breach: true };
    }
  }, [sla, incident?.id, incident?.status, incident?.incident_no, incident?.primary_service_id, serviceMap]);

  const handleSave = async () => {
    if (!form.customer_id || !form.primary_service_id) {
      toast.error('Customer and service are required');
      return;
    }
    // Warn (but allow) when creating an incident without an initial contractor
    if (!isEdit && !form.initial_contractor_id) {
      if (!window.confirm(
        'WARNING — no contractor selected.\n\n'
        + 'The billing timer starts only when a contractor is assigned, so opening a case '
        + 'without an assignment causes lost revenue. If no contractor is assigned within '
        + '5 minutes, the case will be flagged URGENT.\n\n'
        + 'Open the case anyway?',
      )) {
        return;
      }
    }
    setSaving(true);
    try {
      if (isEdit) {
        const { customer_id, primary_service_id, outcome,
                initial_contractor_id, initial_service_id, ...editable } = form;
        // Field-level merge: only PATCH the fields THIS controller actually
        // changed since opening the dialog. Un-touched fields are omitted so
        // simultaneous edits by two controllers don't stomp each other —
        // whoever saves second only overwrites the fields they themselves
        // typed into, everything else keeps the first controller's freshly
        // saved value.
        const diff = {};
        Object.entries(editable).forEach(([key, value]) => {
          if (JSON.stringify(value) !== JSON.stringify(initialForm[key])) {
            diff[key] = value;
          }
        });
        if (Object.keys(diff).length === 0) {
          toast.info('No changes to save');
          setSaving(false);
          onOpenChange(false);
          return;
        }
        await api.patch(`/incidents/${incident.id}`, diff);
        toast.success(
          `Saved ${Object.keys(diff).length} change${Object.keys(diff).length === 1 ? '' : 's'} — other fields left untouched`,
        );
      } else {
        const r = await api.post('/incidents', form);
        toast.success(`Incident ${r.data.incident_no} created${form.initial_contractor_id ? ' with contractor assigned' : ' — assign contractor ASAP'}`);
        onSaved?.(r.data);
        onOpenChange(false);
        return;
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleAddAssignment = async () => {
    if (!newAssign.contractor_id || !newAssign.service_id) {
      toast.error('Pick a contractor and service');
      return;
    }
    setActioning(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/assignments`, newAssign);
      toast.success('Contractor assigned');
      onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleEndAssignment = async (aid) => {
    setActioning(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/assignments/${aid}/end`);
      toast.success('Assignment ended');
      onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleRemoveAssignment = async (aid) => {
    if (!isAdmin) return;
    setActioning(true);
    try {
      const r = await api.delete(`/incidents/${incident.id}/assignments/${aid}`);
      toast.success('Assignment removed');
      onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleClose = async () => {
    if (!closeOutcome.trim()) { toast.error('Outcome required'); return; }
    setActioning(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/close`, { outcome: closeOutcome });
      const next = r.data;
      if (next.status === 'pending_approval') {
        toast.success(`Incident ${next.incident_no} sent for admin approval`);
      } else {
        toast.success(`Incident ${next.incident_no} closed`);
      }
      setCloseDialog(false);
      onSaved?.(next);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Reason required'); return; }
    setActioning(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/cancel`, { reason: cancelReason });
      toast.success(`Incident ${r.data.incident_no} cancelled`);
      setCancelDialog(false);
      onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleClearDuress = async () => {
    setActioning(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/duress-clear`, {});
      toast.success('Duress check cleared — logged to the occurrence book');
      onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleReopen = async () => {
    setActioning(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/reopen`);
      toast.success('Incident reopened');
      onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleDownloadReport = async () => {
    setReportBusy(true);
    try {
      const r = await api.get(`/incidents/${incident.id}/report.pdf`, {
        responseType: 'blob',
        params: { t: Date.now() },
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${incident.incident_no}-report.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download report'); }
    finally { setReportBusy(false); }
  };

  const openReportDialog = () => {
    const cust = customerMap[incident?.customer_id] || {};
    setReportRecipients(cust.email || '');
    setReportNote('');
    setReportDialog(true);
  };

  const handleSendReport = async () => {
    const recipients = reportRecipients.split(/[,;\s]+/).filter(Boolean);
    if (!recipients.length) { toast.error('Enter at least one recipient email'); return; }
    setReportBusy(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/report/send`, {
        recipients, note: reportNote || null,
      });
      toast.success(`Incident report sent to ${r.data.recipients.join(', ')}`);
      setReportDialog(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReportBusy(false); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const MAX_BYTES = 25 * 1024 * 1024;
    const sharedCaption = uploadCaption.trim();
    setActioning(true);
    let ok = 0;
    let failed = 0;
    let lastResponse = null;
    // Sequential to keep the audit log clean and the SSE stream ordered.
    // Compression is done client-side so a batch of 10 phone photos doesn't
    // hammer the server.
    for (const original of files) {
      let file = original;
      try {
        file = await compressImageIfNeeded(original);
      } catch (err) {
        console.warn('compression skipped for', original.name, err);
      }
      if (file.size > MAX_BYTES) {
        const mb = (file.size / (1024 * 1024)).toFixed(1);
        toast.error(`${file.name} too large (${mb} MB, max 25 MB) — skipped`);
        failed += 1;
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      if (sharedCaption) fd.append('caption', sharedCaption);
      try {
        const r = await api.post(`/incidents/${incident.id}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        lastResponse = r.data;
        ok += 1;
      } catch (err) {
        console.error('upload failed for', file.name, err);
        failed += 1;
        toast.error(`${file.name}: ${formatApiError(err.response?.data?.detail) || 'upload failed'}`);
      }
    }
    // One combined summary toast instead of N per file.
    if (ok > 0) {
      toast.success(
        `Uploaded ${ok} file${ok === 1 ? '' : 's'}${failed ? ` (${failed} failed)` : ''}`,
      );
    }
    setUploadCaption('');
    if (lastResponse) onSaved?.(lastResponse);
    setActioning(false);
    e.target.value = '';
  };

  const handleSaveCaption = async (attId) => {
    const text = (captionDrafts[attId] || '').trim();
    setActioning(true);
    try {
      const r = await api.patch(`/incidents/${incident.id}/attachments/${attId}`, { caption: text || null });
      toast.success('Caption saved');
      setCaptionEditing((s) => ({ ...s, [attId]: false }));
      onSaved?.(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleAddNote = async () => {
    const text = newNoteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/notes`, { text });
      setNewNoteText('');
      onSaved?.(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingNote(false); }
  };

  const handleVoidNote = async (noteId) => {
    // Prompt for a mandatory reason — the backend rejects <3 chars.
    const reason = window.prompt(
      'Void this entry? It will remain visible with a strike-through.\n\n'
      + 'Please explain why (e.g. "duplicate", "posted in error", "typo — see next entry"):',
    );
    if (reason === null) return;            // user cancelled
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      toast.error('A void reason of at least 3 characters is required.');
      return;
    }
    setActioning(true);
    try {
      await api.post(`/incidents/${incident.id}/notes/${noteId}/void`, { reason: trimmed });
      const fresh = await api.get(`/incidents/${incident.id}`);
      toast.success('Entry voided');
      onSaved?.(fresh.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text || '');
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleEditNoteSave = async () => {
    const text = editingNoteText.trim();
    if (!text) { toast.error('Note text cannot be empty'); return; }
    setActioning(true);
    try {
      const r = await api.patch(`/incidents/${incident.id}/notes/${editingNoteId}`, { text });
      toast.success('Note updated');
      cancelEditNote();
      onSaved?.(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleDownload = async (att) => {
    try {
      const r = await api.get(`/incidents/${incident.id}/attachments/${att.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const handleDeleteAttachment = async (aid) => {
    if (!isAdmin) return;
    setActioning(true);
    try {
      const r = await api.delete(`/incidents/${incident.id}/attachments/${aid}`);
      toast.success('Attachment deleted');
      onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  // ----- Expenses (S&T / Accommodation / Other) -------------------------
  const expensesTotal = useMemo(
    () => (incident?.expenses || []).reduce((a, e) => a + Number(e.amount || 0), 0),
    [incident?.expenses],
  );

  const resetExpenseForm = () => {
    setNewExpense({ kind: 's_and_t', description: '', amount: '', incurred_on: '' });
    setExpenseReceiptFile(null);
    if (expenseReceiptInputRef.current) expenseReceiptInputRef.current.value = '';
  };

  const handleAddExpense = async () => {
    const desc = (newExpense.description || '').trim();
    const amt = Number(newExpense.amount);
    if (!desc) { toast.error('Description is required'); return; }
    if (!Number.isFinite(amt) || amt < 0) { toast.error('Enter a valid amount'); return; }
    if (expenseReceiptFile && expenseReceiptFile.size > 25 * 1024 * 1024) {
      toast.error('Receipt too large (max 25 MB)');
      return;
    }
    setSavingExpense(true);
    try {
      let receiptAttachmentId = null;
      // Upload receipt first (if provided) so we can link its attachment id.
      if (expenseReceiptFile) {
        let toUpload = expenseReceiptFile;
        try {
          toUpload = await compressImageIfNeeded(expenseReceiptFile);
        } catch (err) {
          console.warn('Receipt image compression failed, uploading original:', err);
        }
        const fd = new FormData();
        fd.append('file', toUpload);
        fd.append('caption', `Receipt — ${desc}`);
        const upload = await api.post(`/incidents/${incident.id}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const atts = upload.data?.attachments || [];
        receiptAttachmentId = atts.length ? atts[atts.length - 1].id : null;
      }
      const payload = {
        kind: newExpense.kind,
        description: desc,
        amount: amt,
        receipt_attachment_id: receiptAttachmentId,
        incurred_on: newExpense.incurred_on || null,
      };
      const r = await api.post(`/incidents/${incident.id}/expenses`, payload);
      toast.success(`Expense added — R ${amt.toFixed(2)}`);
      resetExpenseForm();
      onSaved?.(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingExpense(false); }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    setActioning(true);
    try {
      const r = await api.delete(`/incidents/${incident.id}/expenses/${expenseId}`);
      toast.success('Expense deleted');
      onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActioning(false); }
  };

  const handleDownloadReceipt = async (attachmentId) => {
    const att = (incident?.attachments || []).find((a) => a.id === attachmentId);
    if (!att) { toast.error('Receipt file not found on this incident'); return; }
    await handleDownload(att);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto p-0 gap-0 border-0 shadow-2xl" data-testid="incident-dialog">
        {/* ============ CASE PANEL HEADER ============ */}
        {isEdit ? (
          <div className="relative">
            {/* Gradient accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-[#0D5B69] via-[#0D5B69] to-[#80C030]" />
            <div className="px-6 pt-5 pb-4 bg-gradient-to-br from-[#0D5B69] via-[#0D5B69] to-[#0a4751] text-white">
              <DialogHeader className="space-y-2 text-left">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 shrink-0">
                      <Briefcase className="w-5 h-5 text-[#80C030]" />
                    </div>
                    <div className="min-w-0">
                      <DialogTitle className="font-heading text-2xl text-white font-bold tracking-tight leading-none truncate">
                        {customerMap[incident.customer_id]?.company_name || 'Unknown customer'}
                      </DialogTitle>
                      <DialogDescription className="text-[11px] text-white/70 mt-1 uppercase tracking-wider">
                        {serviceMap[incident.primary_service_id]?.name || '—'} · <span className="font-mono normal-case tracking-normal">{incident.incident_no}</span>
                      </DialogDescription>
                    </div>
                  </div>
                  <div className="flex-1" />
                  <Badge
                    className={`${STATUS_STYLE[incident.status]} hover:opacity-100 px-3 py-1 text-xs font-semibold shadow-sm`}
                    data-testid={`incident-status-${incident.status}`}
                  >
                    {STATUS_LABEL[incident.status]}
                  </Badge>
                </div>

                {/* Inline meta chips: PO + location */}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {incident.po_number && (
                    <span className="inline-flex items-center gap-1.5 text-xs bg-white/10 backdrop-blur-sm border border-white/15 px-2.5 py-1 rounded-md text-white/90">
                      <Receipt className="w-3 h-3 text-[#80C030]" />
                      PO {incident.po_number}
                    </span>
                  )}
                  {incident.location_address && (
                    <span className="inline-flex items-center gap-1.5 text-xs bg-white/10 backdrop-blur-sm border border-white/15 px-2.5 py-1 rounded-md text-white/90 max-w-[420px] truncate">
                      <Paperclip className="w-3 h-3 text-[#80C030] shrink-0" />
                      <span className="truncate">{incident.location_address}</span>
                    </span>
                  )}
                </div>
              </DialogHeader>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-slate-200 border-b border-slate-200">
              <CaseKpi icon={Calendar} label="Opened" value={fmtTime(incident.opened_at)} />
              <CaseKpi
                icon={Clock}
                label={incident.status === 'closed' ? 'Resolved In' : incident.status === 'cancelled' ? 'Cancelled After' : 'Running'}
                value={fmtDuration(incident.opened_at, incident.closed_at || incident.cancelled_at)}
                pulse={['open', 'in_progress'].includes(incident.status)}
              />
              <SlaKpi sla={sla} status={incident.status} />
              <CaseKpi
                icon={Users}
                label="Contractors"
                value={`${(incident.assignments || []).length} (${(incident.assignments || []).filter((a) => !a.ended_at).length} active)`}
              />
              <CaseKpi
                icon={Receipt}
                label={billing?.no_bill ? 'No Bill' : 'Billable Total (incl. VAT)'}
                value={
                  billing == null
                    ? '…'
                    : billing.no_bill
                      ? (billing.no_bill_reason || 'Within cancel window')
                      : fmtR(billing.client_total)
                }
                sub={billing && !billing.no_bill ? `${fmtR(billing.client_subtotal)} ex VAT` : undefined}
                accent
              />
            </div>

            {/* ===== Inverted assignment time window (end before start) → R0.00 bill ===== */}
            {isEdit && (() => {
              const inverted = (incident.assignments || []).filter(
                (a) => a.started_at && a.ended_at && new Date(a.ended_at) < new Date(a.started_at),
              );
              if (inverted.length === 0) return null;
              return (
                <div
                  className="rounded-md bg-red-50 border border-red-200 px-3 py-2.5 flex items-start gap-2 mb-1"
                  data-testid="inverted-timing-warning"
                >
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-red-700 leading-relaxed">
                    <strong>
                      End time is before start time on {inverted.length} assignment
                      {inverted.length > 1 ? 's' : ''} — this is why the billable total is R0.00.
                    </strong>{' '}
                    Check the <strong>date</strong> on the contractor&apos;s end time (a common cross-midnight slip).
                    {incident.status === 'closed'
                      ? ' Use “Edit Timings” to correct it and the bill will recalculate.'
                      : ' Correct the assignment end time and the bill will recalculate.'}
                  </div>
                </div>
              );
            })()}

            {/* ===== DURESS: cancelled from a partner app — confirm the caller is safe ===== */}
            {isEdit && incident.duress_check_required && (
              <div className="bg-red-700 text-white px-4 py-2.5 flex items-center gap-3" data-testid="duress-check-banner">
                <AlertTriangle className="w-5 h-5 shrink-0 animate-pulse" />
                <div className="flex-1 text-xs leading-tight">
                  <p className="font-bold uppercase tracking-wide">Cancelled under duress? — Verify the caller is safe</p>
                  <p className="opacity-90">
                    Cancelled by {incident.cancelled_by_name || 'unknown caller'}
                    {incident.cancelled_by_role ? ` (${incident.cancelled_by_role.replace(/_/g, ' ')})` : ''}
                    {incident.cancel_source ? ` via ${incident.cancel_source}` : ''}
                    {incident.cancel_reason ? ` — “${incident.cancel_reason}”` : ''}. Call the member back before standing down.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7 bg-white text-red-700 hover:bg-slate-100 font-bold text-xs shrink-0"
                  onClick={handleClearDuress}
                  disabled={actioning}
                  data-testid="duress-clear-btn"
                >
                  {actioning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Caller confirmed safe'}
                </Button>
              </div>
            )}
            {isEdit && !incident.duress_check_required && incident.duress_confirmed_at && (
              <div className="bg-green-50 border-b border-green-200 px-4 py-2 flex items-center gap-2" data-testid="duress-cleared-banner">
                <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                <p className="text-[11px] text-green-800">
                  Duress check cleared by <strong>{incident.duress_confirmed_by_name || 'operator'}</strong> on {fmtTime(incident.duress_confirmed_at)} — caller confirmed safe.
                </p>
              </div>
            )}

            {/* ===== URGENT: assignment overdue ===== */}
            {isEdit
              && ['open', 'in_progress'].includes(incident.status)
              && (incident.assignments || []).length === 0
              && (() => {
                const openedTs = incident.opened_at ? new Date(incident.opened_at).getTime() : 0;
                const elapsedMin = openedTs ? (Date.now() - openedTs) / 60000 : 0;
                if (elapsedMin < 5) return null;
                return (
                  <div className="bg-red-600 text-white px-4 py-2.5 flex items-center gap-3 animate-pulse" data-testid="assignment-overdue-banner">
                    <AlertTriangle className="w-5 h-5 shrink-0" />
                    <div className="flex-1 text-xs leading-tight">
                      <p className="font-bold uppercase tracking-wide">URGENT — No Contractor Assigned</p>
                      <p className="opacity-90">{Math.floor(elapsedMin)} minutes since open and no contractor on the case. Billing has not started. Assign now.</p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 bg-white text-red-600 hover:bg-slate-100 font-bold text-xs shrink-0"
                      onClick={() => setActiveTab('assignments')}
                      data-testid="go-to-assign-btn"
                    >
                      Assign now
                    </Button>
                  </div>
                );
              })()}

            {/* ===== ACTIVITY TICKER ===== */}
            {activity.length > 0 && (
              <div
                className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex items-center gap-2 overflow-x-auto"
                data-testid="incident-activity-ticker"
              >
                <span className="shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#0D5B69]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#80C030] animate-pulse" />
                  Activity
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {activity.map((e, idx) => (
                    <ActivityChip key={`${e.at}-${idx}`} entry={e} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="px-6 pt-6 pb-4 border-b border-slate-200">
            <DialogHeader className="text-left space-y-1">
              <DialogTitle className="font-heading text-xl text-[#0D5B69] flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-[#80C030]" /> New Incident
              </DialogTitle>
              <DialogDescription>
                Log a new incident — billing starts the moment the case is logged.
              </DialogDescription>
            </DialogHeader>
          </div>
        )}

        <div className="px-6 pb-6 pt-4">

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-0">
          <TabsList>
            <TabsTrigger value="details" data-testid="tab-incident-details">Details</TabsTrigger>
            {isEdit && <TabsTrigger value="assignments" data-testid="tab-incident-assignments">Assignments ({(incident.assignments || []).length})</TabsTrigger>}
            {isEdit && <TabsTrigger value="attachments" data-testid="tab-incident-attachments">Attachments ({(incident.attachments || []).length})</TabsTrigger>}
            {isEdit && <TabsTrigger value="notes" data-testid="tab-incident-notes">Notes ({(incident.notes || []).length})</TabsTrigger>}
            {isEdit && <TabsTrigger value="expenses" data-testid="tab-incident-expenses">Expenses ({(incident.expenses || []).length})</TabsTrigger>}
            <TabsTrigger value="sop" data-testid="tab-incident-sop">SOP</TabsTrigger>
          </TabsList>

          {/* ========== DETAILS ========== */}
          <TabsContent value="details" className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Customer *">
                <Select
                  value={form.customer_id}
                  onValueChange={(v) => setForm({ ...form, customer_id: v })}
                  disabled={isEdit}
                >
                  <SelectTrigger data-testid="incident-customer-select">
                    <SelectValue placeholder="Pick a customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Primary Service *">
                <Select
                  value={form.primary_service_id}
                  onValueChange={(v) => setForm({ ...form, primary_service_id: v })}
                  disabled={isEdit}
                >
                  <SelectTrigger data-testid="incident-service-select">
                    <SelectValue placeholder="Pick a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="PO Number" className={customerMap[form.customer_id]?.po_required ? '' : 'opacity-70'}>
                <Input
                  value={form.po_number || ''}
                  onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                  placeholder={customerMap[form.customer_id]?.po_required ? 'Required for this customer' : 'Optional'}
                  disabled={isLocked}
                  data-testid="incident-po-input"
                />
              </Field>
              <Field label="End Client">
                <Input
                  value={form.end_client_name || ''}
                  onChange={(e) => setForm({ ...form, end_client_name: e.target.value })}
                  placeholder="Who is the customer servicing? eg. DSV Mounties"
                  disabled={isLocked}
                  data-testid="incident-end-client-input"
                />
              </Field>
            </div>

            <Field label="Description">
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What's happening, what needs doing"
                disabled={isLocked}
                data-testid="incident-description-input"
              />
            </Field>

            <Field label="Location">
              <LocationPicker
                address={form.location_address}
                lat={form.location_lat}
                lng={form.location_lng}
                onChange={({ address, lat, lng }) => setForm((s) => ({
                  ...s, location_address: address ?? s.location_address,
                  location_lat: lat ?? s.location_lat, location_lng: lng ?? s.location_lng,
                }))}
                readOnly={isLocked}
              />
            </Field>

            {/* ========== VEHICLE OF INTEREST (optional) ========== */}
            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-3" data-testid="vehicle-block">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide font-semibold text-[#0D5B69]">
                  Vehicle of Interest <span className="text-[10px] text-slate-400 font-normal normal-case ml-1">— optional, populate for SVR / hijack cases</span>
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Make">
                  <Select
                    value={form.vehicle_make || '__none__'}
                    onValueChange={(v) => setForm({ ...form, vehicle_make: v === '__none__' ? '' : v })}
                    disabled={isLocked}
                  >
                    <SelectTrigger data-testid="vehicle-make-select"><SelectValue placeholder="Pick make" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {vehicleCatalogue.makes.map((m) => (
                        <SelectItem key={m.id} value={m.label}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Colour">
                  <Select
                    value={form.vehicle_colour || '__none__'}
                    onValueChange={(v) => setForm({ ...form, vehicle_colour: v === '__none__' ? '' : v })}
                    disabled={isLocked}
                  >
                    <SelectTrigger data-testid="vehicle-colour-select"><SelectValue placeholder="Pick colour" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {vehicleCatalogue.colours.map((c) => (
                        <SelectItem key={c.id} value={c.label}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Type">
                  <Select
                    value={form.vehicle_type || '__none__'}
                    onValueChange={(v) => setForm({ ...form, vehicle_type: v === '__none__' ? '' : v })}
                    disabled={isLocked}
                  >
                    <SelectTrigger data-testid="vehicle-type-select"><SelectValue placeholder="Pick type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {vehicleCatalogue.types.map((t) => (
                        <SelectItem key={t.id} value={t.label}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Model">
                  <Input
                    value={form.vehicle_model || ''}
                    onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                    placeholder="e.g. Hilux 2.8 GD-6"
                    disabled={isLocked}
                    data-testid="vehicle-model-input"
                  />
                </Field>
                <Field label="Registration">
                  <Input
                    value={form.vehicle_registration || ''}
                    onChange={(e) => setForm({ ...form, vehicle_registration: e.target.value.toUpperCase() })}
                    placeholder="e.g. ABC 123 GP"
                    disabled={isLocked}
                    className="font-mono uppercase tracking-wider"
                    data-testid="vehicle-registration-input"
                  />
                </Field>
              </div>
            </div>
            {/* ========== END VEHICLE BLOCK ========== */}

            {/* ========== INITIAL CONTRACTOR (NEW INCIDENT ONLY) ========== */}
            {!isEdit && (
              <div className="rounded-md border-2 border-[#80C030] bg-[#F1F7E5] p-3 space-y-3" data-testid="initial-contractor-block">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#80C030] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">!</div>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-[#0D5B69] uppercase tracking-wide">Assign Contractor (strongly recommended)</p>
                    <p className="text-[11px] text-slate-700 mt-0.5 leading-snug">
                      The billing timer starts the moment a contractor is assigned — not when the case is opened.
                      Assign now or risk under-billing. If no contractor is assigned within 5 minutes, the case is flagged URGENT.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Select
                    value={form.initial_contractor_id || '__none__'}
                    onValueChange={(v) => setForm({ ...form, initial_contractor_id: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger data-testid="initial-contractor-select"><SelectValue placeholder="Pick contractor" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__none__">— Assign later (NOT recommended) —</SelectItem>
                      {(contractors || []).filter((c) => c.active).map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.company_name}{c.area_of_operation ? ` · ${c.area_of_operation}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={form.initial_service_id || form.primary_service_id || '__same__'}
                    onValueChange={(v) => setForm({ ...form, initial_service_id: v === '__same__' ? '' : v })}
                    disabled={!form.initial_contractor_id}
                  >
                    <SelectTrigger data-testid="initial-service-select"><SelectValue placeholder="Service for this assignment" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__same__">Same as primary service</SelectItem>
                      {(services || []).filter((s) => s.active).map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {/* ========== END INITIAL CONTRACTOR ========== */}

            {isEdit && incident.status === 'pending_approval' && (
              <div className="bg-violet-50 border border-violet-200 rounded-md p-3 text-sm" data-testid="pending-approval-banner">
                <p className="font-semibold text-violet-800 flex items-center gap-1">
                  <Clock className="w-4 h-4" /> Pending admin approval
                </p>
                <p className="text-violet-700 mt-1">
                  Operator outcome: <i>{incident.outcome || '—'}</i>
                </p>
                <p className="text-xs text-violet-600 mt-1">
                  Requested {fmtTime(incident.close_requested_at)}. An admin must confirm timings before the case is closed and the bill is finalised.
                </p>
              </div>
            )}
            {isEdit && incident.status === 'in_progress' && incident.close_reject_reason && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm" data-testid="close-reject-banner">
                <p className="font-semibold text-red-800 flex items-center gap-1">
                  <XCircle className="w-4 h-4" /> Previous close request rejected
                </p>
                <p className="text-red-700 mt-1">{incident.close_reject_reason}</p>
              </div>
            )}
            {isEdit && incident.status === 'cancelled' && (
              <div className="bg-slate-100 border border-slate-200 rounded-md p-3 text-sm">
                <p className="font-semibold text-slate-700">Cancellation reason</p>
                <p className="text-slate-600 mt-1">{incident.cancel_reason}</p>
              </div>
            )}
            {isEdit && incident.status === 'closed' && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm">
                <p className="font-semibold text-green-800">Outcome</p>
                <p className="text-green-700 mt-1">{incident.outcome}</p>
                <p className="text-xs text-green-600 mt-1">
                  Closed {fmtTime(incident.closed_at)}
                  {incident.close_approved_by && ' · admin approved'}
                </p>
              </div>
            )}
          </TabsContent>

          {/* ========== ASSIGNMENTS ========== */}
          {isEdit && (
            <TabsContent value="assignments" className="space-y-3 mt-4">
              {(incident.assignments || []).length > 0 && (
                <div className="rounded-md bg-[#0D5B69]/[0.04] border border-[#0D5B69]/15 px-3 py-2 flex items-start gap-2" data-testid="multi-contractor-billing-banner">
                  <Receipt className="w-4 h-4 text-[#0D5B69] mt-0.5 shrink-0" />
                  <p className="text-[11px] text-[#0D5B69] leading-snug">
                    <strong>Each contractor runs its own billing clock</strong> from the moment they&apos;re assigned. The case charge to the client is the sum of every contractor&apos;s billable time.
                  </p>
                </div>
              )}
              {!isLocked && (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex flex-wrap gap-2 items-end" data-testid="add-assignment-block">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-[10px] uppercase tracking-wide text-slate-500">Contractor / Employee / Vehicle</Label>
                    <Select value={newAssign.contractor_id} onValueChange={(v) => setNewAssign((s) => ({ ...s, contractor_id: v }))}>
                      <SelectTrigger className="h-9" data-testid="assign-contractor-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {contractors.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="flex items-center gap-2">
                              <span>{c.company_name}</span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold border ${c.kind === 'vehicle' ? 'bg-[#80C030]/15 text-[#5a8c20] border-[#80C030]/30' : c.kind === 'employee' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-[#0D5B69]/10 text-[#0D5B69] border-[#0D5B69]/20'}`}>
                                {(c.kind || 'company').toUpperCase()}
                              </span>
                              {c.kind === 'vehicle' && c.registration_number && (
                                <span className="text-[10px] font-mono text-slate-500">· {c.registration_number}</span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <Label className="text-[10px] uppercase tracking-wide text-slate-500">Service</Label>
                    <Select value={newAssign.service_id} onValueChange={(v) => setNewAssign((s) => ({ ...s, service_id: v }))}>
                      <SelectTrigger className="h-9" data-testid="assign-service-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleAddAssignment}
                    disabled={actioning}
                    className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold"
                    data-testid="add-assignment-btn"
                  >
                    <Plus className="w-4 h-4" /> Assign
                  </Button>
                </div>
              )}

              {(incident.assignments || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-md">
                  No contractors assigned yet
                </p>
              ) : (
                <div className="space-y-2">
                  {(incident.assignments || []).map((a, idx) => {
                    const contractor = contractorMap[a.contractor_id];
                    const service = serviceMap[a.service_id];
                    const open = !a.ended_at;
                    const kind = contractor?.kind || 'company';
                    const kindCls = kind === 'vehicle' ? 'bg-[#80C030]/15 text-[#5a8c20] border-[#80C030]/30' : kind === 'employee' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-[#0D5B69]/10 text-[#0D5B69] border-[#0D5B69]/20';
                    return (
                      <div key={a.id} className="border border-slate-200 rounded-md p-3 space-y-1.5" data-testid={`assignment-${idx}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-slate-900">{contractor?.company_name || 'Unknown'}</p>
                            <Badge className={`text-[10px] font-semibold border ${kindCls} hover:opacity-100`}>
                              {kind.toUpperCase()}
                            </Badge>
                            {kind === 'vehicle' && contractor?.registration_number && (
                              <span className="font-mono text-xs font-bold text-[#5a8c20]">{contractor.registration_number}</span>
                            )}
                            {open ? (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100 gap-1 animate-pulse">
                                <Clock className="w-3 h-3" /> Billing
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">Ended</Badge>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {open && !isLocked && (
                              <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => handleEndAssignment(a.id)} data-testid={`end-assignment-${idx}`}>
                                <Square className="w-3 h-3" /> End
                              </Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => handleRemoveAssignment(a.id)} data-testid={`remove-assignment-${idx}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-xs text-slate-500">
                          <div><span className="font-semibold text-slate-700">Service:</span> {service?.name || '—'}</div>
                          <div><span className="font-semibold text-slate-700">Started:</span> {fmtTime(a.started_at)}</div>
                          <div><span className="font-semibold text-slate-700">{open ? 'Running' : 'Ended'}:</span> {open ? fmtDuration(a.started_at) : fmtTime(a.ended_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          )}

          {/* ========== ATTACHMENTS ========== */}
          {isEdit && (
            <TabsContent value="attachments" className="space-y-3 mt-4">
              <div className="flex justify-between items-end gap-3">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Caption for next upload (optional)</Label>
                  <Input
                    value={uploadCaption}
                    onChange={(e) => setUploadCaption(e.target.value)}
                    placeholder="e.g. Forced entry damage to side door"
                    className="mt-1 h-9 text-sm"
                    data-testid="attachment-caption-input"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Select one file <b>or hold Ctrl / Cmd to pick many at once</b>. Photos auto-compressed to ≤ 2 MB · PDF/doc max 25 MB · caption (if any) applies to every file in this batch.</p>
                </div>
                {!isLocked && (
                  <label className="cursor-pointer shrink-0">
                    <input type="file" hidden multiple onChange={handleUpload} accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.txt" data-testid="attachment-file-input" />
                    <span className="inline-flex items-center gap-1 bg-[#80C030] hover:bg-[#6FA528] text-white text-sm font-semibold px-3 py-2 rounded-md">
                      <Upload className="w-4 h-4" /> Upload files
                    </span>
                  </label>
                )}
              </div>
              {(incident.attachments || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-md">
                  No attachments yet
                </p>
              ) : (
                <div className="space-y-2">
                  {(incident.attachments || []).map((att, idx) => {
                    const IconCmp = att.content_type?.startsWith('image/') ? ImageIcon : att.content_type === 'application/pdf' ? FileText : Paperclip;
                    const editing = captionEditing[att.id];
                    return (
                      <div key={att.id} className="border border-slate-200 rounded-md p-2.5 space-y-2" data-testid={`attachment-${idx}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-slate-100 rounded flex items-center justify-center shrink-0"><IconCmp className="w-4 h-4 text-slate-500" /></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{att.filename}</p>
                            <p className="text-xs text-slate-500">{(att.size_bytes / 1024).toFixed(1)} KB · {fmtTime(att.uploaded_at)}</p>
                          </div>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleDownload(att)} data-testid={`download-attachment-${idx}`}>
                            <Download className="w-4 h-4 text-slate-500" />
                          </Button>
                          {!isLocked && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2 text-xs"
                              onClick={() => {
                                setCaptionDrafts((s) => ({ ...s, [att.id]: att.caption || '' }));
                                setCaptionEditing((s) => ({ ...s, [att.id]: !editing }));
                              }}
                              data-testid={`edit-attachment-caption-${idx}`}
                            >
                              {editing ? 'Done' : (att.caption ? 'Edit caption' : 'Add caption')}
                            </Button>
                          )}
                          {isAdmin && (
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDeleteAttachment(att.id)} data-testid={`delete-attachment-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                        {editing ? (
                          <div className="flex gap-2 pl-12">
                            <Input
                              value={captionDrafts[att.id] ?? ''}
                              onChange={(e) => setCaptionDrafts((s) => ({ ...s, [att.id]: e.target.value }))}
                              placeholder="What does this photo / file show?"
                              className="h-8 text-sm"
                              data-testid={`attachment-caption-edit-${idx}`}
                            />
                            <Button size="sm" onClick={() => handleSaveCaption(att.id)} disabled={actioning} className="h-8 bg-[#80C030] hover:bg-[#6FA528] text-white" data-testid={`save-attachment-caption-${idx}`}>
                              Save
                            </Button>
                          </div>
                        ) : att.caption ? (
                          <p className="text-xs text-slate-600 italic pl-12" data-testid={`attachment-caption-${idx}`}>“{att.caption}”</p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          )}

          {/* ========== NOTES ========== */}
          {isEdit && (
            <TabsContent value="notes" className="space-y-3 mt-4" data-testid="notes-tab">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Add operator note</Label>
                  <span className="text-[10px] text-slate-400">Tap a phrase to pre-fill</span>
                </div>

                {/* Quick-tap template chips */}
                {noteTemplates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="note-template-chips">
                    {noteTemplates.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          const sep = newNoteText && !newNoteText.endsWith(' ') && !newNoteText.endsWith('\n') ? ' ' : '';
                          setNewNoteText((prev) => `${prev}${sep}${t.text}`);
                        }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#0D5B69]/20 bg-white hover:bg-[#80C030]/10 hover:border-[#80C030]/50 text-[11px] font-medium text-[#0D5B69] transition-all"
                        data-testid={`note-template-${t.id}`}
                        title={t.text}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#80C030]" />
                        {t.label}
                      </button>
                    ))}
                  </div>
                )}

                <Textarea
                  rows={2}
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  placeholder="e.g. Controller called the client at 14:35 — vehicle recovered, no further action required."
                  data-testid="new-note-input"
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleAddNote();
                  }}
                />
                <div className="flex items-center justify-between">
                  <p className="text-[11px] text-slate-400">⌘/Ctrl + Enter to save · Notes appear on the client incident report.</p>
                  <Button
                    size="sm"
                    onClick={handleAddNote}
                    disabled={savingNote || !newNoteText.trim()}
                    className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1"
                    data-testid="add-note-btn"
                  >
                    {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Note
                  </Button>
                </div>
              </div>
              <div className="space-y-2 pt-2" key={`notes-${(incident.notes || []).length}-${(incident.notes || []).map((n) => n.id).join(',')}`}>
                {(incident.notes || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-md">
                    No operator notes yet — add the first one above.
                  </p>
                ) : (
                  [...(incident.notes || [])]
                    .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''))
                    .map((n, idx) => {
                      const mine = n.author_id === user?.id;
                      const voided = !!n.voided_at;
                      // 10-min self-edit window; after that entries lock.
                      const ageMin = n.created_at
                        ? (Date.now() - new Date(n.created_at).getTime()) / 60000
                        : 999;
                      const canEditText = mine && !voided && ageMin <= 10;
                      // Any author can void their own; admins can void anyone's.
                      const canVoid = (mine || isAdmin) && !voided;
                      const isEditing = editingNoteId === n.id;
                      return (
                        <div key={n.id}
                             className={`border rounded-md p-3 ${voided ? 'bg-slate-100/60 border-slate-200' : 'bg-slate-50/50 border-slate-200'}`}
                             data-testid={`note-row-${idx}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                                <span className="font-semibold text-[#0D5B69]">{fmtTime(n.created_at)}</span>
                                <span>·</span>
                                <span>{n.author_name || 'operator'}</span>
                                {mine && <Badge className="bg-[#80C030]/10 text-[#5a8c20] border-[#80C030]/30 text-[10px] py-0">you</Badge>}
                                {n.edited_at && !voided && (
                                  <span className="italic text-slate-400">· edited {fmtTime(n.edited_at)}</span>
                                )}
                                {voided && (
                                  <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px] py-0" data-testid={`note-voided-${idx}`}>VOIDED</Badge>
                                )}
                              </div>
                              {isEditing ? (
                                <div className="mt-2 space-y-2">
                                  <Textarea
                                    rows={2}
                                    value={editingNoteText}
                                    onChange={(e) => setEditingNoteText(e.target.value)}
                                    data-testid={`edit-note-input-${idx}`}
                                    autoFocus
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button size="sm" variant="outline" onClick={cancelEditNote} data-testid={`cancel-edit-note-${idx}`}>Cancel</Button>
                                    <Button
                                      size="sm"
                                      onClick={handleEditNoteSave}
                                      disabled={actioning || !editingNoteText.trim()}
                                      className="bg-[#80C030] hover:bg-[#6FA528] text-white"
                                      data-testid={`save-edit-note-${idx}`}
                                    >
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              ) : voided ? (
                                <>
                                  <p className="text-sm text-slate-400 mt-1 whitespace-pre-wrap break-words line-through">
                                    {n.text}
                                  </p>
                                  <p className="text-xs italic text-red-600 mt-1.5">
                                    Voided by {n.voided_by_name || 'operator'}
                                    {n.voided_at && <> on {fmtTime(n.voided_at)}</>} —{' '}
                                    <span className="text-slate-600">reason: {n.voided_reason || '—'}</span>
                                  </p>
                                </>
                              ) : (
                                <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap break-words">{n.text}</p>
                              )}
                            </div>
                            {!isEditing && !voided && (
                              <div className="flex items-center gap-0.5 shrink-0">
                                {canEditText && (
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500"
                                          onClick={() => startEditNote(n)} data-testid={`edit-note-${idx}`}
                                          title={`Edit (within 10 min of posting — ${Math.max(0, 10 - Math.floor(ageMin))} min remaining)`}>
                                    <Edit className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {canVoid && (
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
                                          onClick={() => handleVoidNote(n.id)} data-testid={`void-note-${idx}`}
                                          title="Void entry (strikes through — entry stays in the record)">
                                    <Ban className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </TabsContent>
          )}

          {/* ========== EXPENSES — internal S&T / Accommodation / Other cost (not billed) ========== */}
          {isEdit && (
            <TabsContent value="expenses" className="space-y-3 mt-4" data-testid="expenses-tab">
              <div className="flex items-center justify-between gap-2 rounded-md bg-[#0D5B69]/5 border border-[#0D5B69]/15 px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Wallet className="w-4 h-4 text-[#0D5B69]" />
                  <span>
                    Internal expenses (S&amp;T / Accommodation / Other) are <b>not</b> billed to the client. They reduce gross profit on this incident.
                  </span>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Total</div>
                  <div className="font-mono text-sm font-semibold text-[#0D5B69]" data-testid="expenses-total">
                    {fmtR(expensesTotal)}
                  </div>
                </div>
              </div>

              {!isLocked && (
                <div className="border border-slate-200 rounded-md p-3 space-y-3 bg-white" data-testid="expense-add-form">
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-12 sm:col-span-3">
                      <Label className="text-[10px] uppercase tracking-wide text-slate-500">Kind</Label>
                      <Select
                        value={newExpense.kind}
                        onValueChange={(v) => setNewExpense((s) => ({ ...s, kind: v }))}
                      >
                        <SelectTrigger className="h-9 mt-1" data-testid="expense-kind-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="s_and_t">S&amp;T</SelectItem>
                          <SelectItem value="accommodation">Accommodation</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-12 sm:col-span-5">
                      <Label className="text-[10px] uppercase tracking-wide text-slate-500">Description *</Label>
                      <Input
                        value={newExpense.description}
                        onChange={(e) => setNewExpense((s) => ({ ...s, description: e.target.value }))}
                        placeholder="e.g. Overnight stay – Bloemfontein"
                        className="h-9 mt-1 text-sm"
                        maxLength={200}
                        data-testid="expense-description-input"
                      />
                    </div>
                    <div className="col-span-6 sm:col-span-2">
                      <Label className="text-[10px] uppercase tracking-wide text-slate-500">Amount (R) *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newExpense.amount}
                        onChange={(e) => setNewExpense((s) => ({ ...s, amount: e.target.value }))}
                        placeholder="0.00"
                        className="h-9 mt-1 text-sm font-mono"
                        data-testid="expense-amount-input"
                      />
                    </div>
                    <div className="col-span-6 sm:col-span-2">
                      <Label className="text-[10px] uppercase tracking-wide text-slate-500">Date</Label>
                      <Input
                        type="date"
                        value={newExpense.incurred_on}
                        onChange={(e) => setNewExpense((s) => ({ ...s, incurred_on: e.target.value }))}
                        className="h-9 mt-1 text-sm"
                        data-testid="expense-date-input"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px] uppercase tracking-wide text-slate-500">Receipt (optional)</Label>
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          ref={expenseReceiptInputRef}
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                          onChange={(e) => setExpenseReceiptFile(e.target.files?.[0] || null)}
                          className="block text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
                          data-testid="expense-receipt-input"
                        />
                        {expenseReceiptFile && (
                          <Button
                            size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600"
                            onClick={() => {
                              setExpenseReceiptFile(null);
                              if (expenseReceiptInputRef.current) expenseReceiptInputRef.current.value = '';
                            }}
                            data-testid="expense-receipt-clear"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Photos auto-compressed to ≤ 2 MB · PDF/document max 25 MB · saved to incident attachments.</p>
                    </div>
                    <Button
                      onClick={handleAddExpense}
                      disabled={savingExpense}
                      className="gap-1 bg-[#80C030] hover:bg-[#6FA528] text-white shrink-0"
                      data-testid="expense-add-btn"
                    >
                      {savingExpense ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Add expense
                    </Button>
                  </div>
                </div>
              )}

              {(incident.expenses || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-md" data-testid="expenses-empty">
                  No expenses logged on this incident yet.
                </p>
              ) : (
                <div className="space-y-2" data-testid="expenses-list">
                  {[...(incident.expenses || [])]
                    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                    .map((exp, idx) => {
                      const KindIcon = exp.kind === 'accommodation' ? Hotel
                        : exp.kind === 'other' ? MoreHorizontal : Plane;
                      const kindLabel = exp.kind === 's_and_t' ? 'S&T'
                        : exp.kind === 'accommodation' ? 'Accommodation' : 'Other';
                      const receiptAtt = exp.receipt_attachment_id
                        ? (incident.attachments || []).find((a) => a.id === exp.receipt_attachment_id)
                        : null;
                      const canDelete = isAdmin || exp.created_by === user?.id;
                      return (
                        <div
                          key={exp.id}
                          className="border border-slate-200 rounded-md p-3 flex items-start gap-3"
                          data-testid={`expense-row-${idx}`}
                        >
                          <div className="w-9 h-9 bg-[#0D5B69]/10 rounded flex items-center justify-center shrink-0">
                            <KindIcon className="w-4 h-4 text-[#0D5B69]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="text-[10px] border-[#0D5B69]/30 text-[#0D5B69]">{kindLabel}</Badge>
                              <span className="text-sm font-medium text-slate-900">{exp.description}</span>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              {exp.incurred_on ? `Incurred ${(exp.incurred_on || '').slice(0, 10)} · ` : ''}
                              Added {fmtTime(exp.created_at)}
                            </p>
                            {receiptAtt && (
                              <button
                                type="button"
                                onClick={() => handleDownloadReceipt(receiptAtt.id)}
                                className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#0D5B69] hover:underline"
                                data-testid={`expense-receipt-link-${idx}`}
                              >
                                <Receipt className="w-3 h-3" /> {receiptAtt.filename}
                              </button>
                            )}
                            {exp.receipt_attachment_id && !receiptAtt && (
                              <p className="mt-1 text-[11px] text-slate-400 italic">Receipt file no longer attached.</p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-mono text-sm font-semibold text-slate-900" data-testid={`expense-amount-${idx}`}>
                              {fmtR(exp.amount || 0)}
                            </div>
                            {!isLocked && canDelete && (
                              <Button
                                size="sm" variant="ghost"
                                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 mt-1"
                                onClick={() => handleDeleteExpense(exp.id)}
                                data-testid={`expense-delete-${idx}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </TabsContent>
          )}

          {/* ========== SOP — customer Standard Operating Procedure (read-only here) ========== */}
          <TabsContent value="sop" className="space-y-3 mt-4" data-testid="incident-sop-tab">
            {(() => {
              const cust = customerMap[incident?.customer_id];
              const sop = cust?.sop?.trim();
              if (!incident?.customer_id) {
                return (
                  <div className="text-center py-12 text-slate-400 text-sm" data-testid="sop-empty-no-customer">
                    Select a customer in the Details tab to view their Standard Operating Procedure.
                  </div>
                );
              }
              if (!sop) {
                return (
                  <div className="rounded-md border border-dashed border-slate-300 bg-slate-50/60 p-8 text-center" data-testid="sop-empty">
                    <p className="text-sm text-slate-600 font-semibold">No SOP recorded for {cust?.company_name || 'this customer'}.</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Admins can add one under <strong>Customers → {cust?.company_name || 'Customer'} → SOP</strong>.
                    </p>
                  </div>
                );
              }
              return (
                <div data-testid="sop-content">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs uppercase tracking-wide text-slate-600">
                      Standard Operating Procedure
                    </Label>
                    <span className="text-[10px] text-slate-400 font-mono">{cust.company_name}</span>
                  </div>
                  <div
                    className="rounded-md border border-[#0D5B69]/15 bg-[#0D5B69]/[0.03] p-4 max-h-[60vh] overflow-y-auto text-sm text-slate-800 font-mono leading-relaxed whitespace-pre-wrap break-words"
                    data-testid="sop-body"
                  >
                    {sop}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2 italic">
                    SOP managed by admin from the customer profile. Last updated whenever the customer record is saved.
                  </p>
                </div>
              );
            })()}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4 flex-wrap gap-2">
          {isEdit && (
            <>
              <Button
                variant="outline"
                onClick={handleDownloadReport}
                disabled={reportBusy}
                className="gap-1 border-[#0D5B69] text-[#0D5B69] hover:bg-[#0D5B69]/5"
                data-testid="download-incident-report-btn"
                title="Download incident report PDF"
              >
                {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download Report
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={openReportDialog}
                  className="gap-1 border-[#80C030] text-[#5a8c20] hover:bg-[#80C030]/10"
                  data-testid="send-incident-report-btn"
                  title="Email incident report to client (admin only)"
                >
                  <Mail className="w-4 h-4" /> Email to Client
                </Button>
              )}
            </>
          )}
          {isEdit && (incident.status === 'in_progress' || incident.status === 'open') && (
            <Button
              variant="outline"
              onClick={() => setCloseDialog(true)}
              className="gap-1 text-green-700 hover:bg-green-50 border-green-200 border-2 font-semibold"
              data-testid="open-close-dialog-btn"
              title={isAdmin ? 'Close this case (you will review timings before final approval)' : 'Send this case to admin for close-approval'}
            >
              <CheckCircle2 className="w-4 h-4" />
              Close Incident{isAdmin ? '' : ' (Request)'}
            </Button>
          )}
          {isEdit && incident.status === 'pending_approval' && isAdmin && (
            <Button
              variant="outline"
              onClick={() => setApprovalDialog({ open: true, mode: 'approve' })}
              className="gap-1 text-green-700 hover:bg-green-50 border-green-200"
              data-testid="open-approval-dialog-btn"
            >
              <CheckCircle2 className="w-4 h-4" /> Review &amp; Approve
            </Button>
          )}
          {isEdit && incident.status === 'pending_approval' && !isAdmin && (
            <div className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded px-3 py-1.5"
                 data-testid="pending-approval-note">
              Awaiting admin approval to close
            </div>
          )}
          {isEdit && incident.status === 'closed' && isAdmin && (
            <Button
              variant="outline"
              onClick={() => setApprovalDialog({ open: true, mode: 'edit-timings' })}
              className="gap-1 text-[#0D5B69] border-[#0D5B69]/40 hover:bg-[#0D5B69]/5"
              data-testid="edit-timings-btn"
            >
              <Edit className="w-4 h-4" /> Edit Timings
            </Button>
          )}
          {isEdit && !isLocked && incident.status !== 'pending_approval' && (
            <Button variant="outline" onClick={() => setCancelDialog(true)} className="gap-1 text-red-700 hover:bg-red-50 border-red-200" data-testid="open-cancel-dialog-btn">
              <XCircle className="w-4 h-4" /> Cancel Incident
            </Button>
          )}
          {isEdit && isLocked && isAdmin && (
            <Button variant="outline" onClick={handleReopen} className="gap-1" data-testid="reopen-btn">
              <RotateCw className="w-4 h-4" /> Reopen (Admin)
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dismiss-dialog-btn">Dismiss</Button>
          {!isLocked && (
            <Button onClick={handleSave} disabled={saving} className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold" data-testid="save-incident-btn">
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Incident'}
            </Button>
          )}
        </DialogFooter>

        {/* Close-incident sub-dialog */}
        <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#0D5B69]">
                {isAdmin ? 'Close Incident' : 'Request Close'}
              </DialogTitle>
              <DialogDescription>
                {isAdmin
                  ? 'Record the outcome. As an admin you will be taken to the timing-confirmation step before final close.'
                  : 'Record the outcome and send to an admin for approval. Any open assignments will be auto-ended.'}
              </DialogDescription>
            </DialogHeader>
            <Textarea
              rows={3}
              placeholder="e.g. Vehicle recovered, no further action required"
              value={closeOutcome}
              onChange={(e) => setCloseOutcome(e.target.value)}
              data-testid="close-outcome-input"
            />
            {incident?.close_reject_reason && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-1.5">
                <b>Previously rejected:</b> {incident.close_reject_reason}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCloseDialog(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  // Operator path: just request close. Admin path: request, then open approval modal.
                  if (!closeOutcome.trim()) { toast.error('Outcome required'); return; }
                  setActioning(true);
                  try {
                    const r = await api.post(`/incidents/${incident.id}/close`, { outcome: closeOutcome });
                    onSaved?.(r.data);
                    setCloseDialog(false);
                    if (isAdmin) {
                      // Step into the approval/timing-confirmation modal immediately
                      setApprovalDialog({ open: true, mode: 'approve' });
                    } else {
                      toast.success(`Incident ${r.data.incident_no} sent for admin approval`);
                    }
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                  finally { setActioning(false); }
                }}
                disabled={actioning}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                data-testid="confirm-close-btn"
              >
                {isAdmin ? 'Next — Review Timings' : 'Send for Approval'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Close-approval / Edit-timings dialog (admin) */}
        <CloseApprovalDialog
          open={approvalDialog.open}
          onOpenChange={(o) => setApprovalDialog((s) => ({ ...s, open: o }))}
          incident={incident}
          contractors={contractors}
          services={services}
          mode={approvalDialog.mode}
          onSaved={onSaved}
        />

        {/* Cancel-incident sub-dialog */}
        <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-700">Cancel Incident</DialogTitle>
              <DialogDescription>
                Cancelled cases are <strong>never billed</strong>, regardless of contractor time consumed.
                If billing should apply (a contractor was on the road and consumed time),
                <strong> close the case</strong> instead and CSG can decide whether to issue a goodwill credit.
              </DialogDescription>
            </DialogHeader>

            {/* Warn the operator if there are any assignments — they're about to lose billable revenue */}
            {(incident?.assignments || []).length > 0 && (
              <div className="rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2.5" data-testid="cancel-with-assignments-warning">
                <p className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                  ⚠ Contractor(s) Were Assigned
                </p>
                <p className="text-xs text-amber-900 mt-1 leading-snug">
                  Cancelling now will zero the billing on this case. If contractor time should be charged
                  to the customer, click <strong>Back</strong> and use <strong>Close case</strong> instead.
                </p>
              </div>
            )}

            <Textarea
              rows={3}
              placeholder="Why is this being cancelled?"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              data-testid="cancel-reason-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialog(false)}>Back</Button>
              <Button onClick={handleCancel} disabled={actioning} className="bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="confirm-cancel-btn">
                Confirm Cancel (no billing)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send incident report sub-dialog */}
        <Dialog open={reportDialog} onOpenChange={setReportDialog}>
          <DialogContent className="max-w-md" data-testid="send-report-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#0D5B69] flex items-center gap-2">
                <Mail className="w-5 h-5" /> Email Incident Report
              </DialogTitle>
              <DialogDescription>
                Sends a branded PDF — timeline, response team, photos, billing — to the recipient(s) below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Recipients</Label>
                <Textarea
                  rows={2}
                  value={reportRecipients}
                  onChange={(e) => setReportRecipients(e.target.value)}
                  placeholder="client@example.co.za, ops@client.co.za"
                  data-testid="report-recipients-input"
                />
                <p className="text-[11px] text-slate-400">Comma- or space-separated. Pre-filled from customer record.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Cover note (optional)</Label>
                <Textarea
                  rows={3}
                  value={reportNote}
                  onChange={(e) => setReportNote(e.target.value)}
                  placeholder="Goes in the email body, above the attached PDF."
                  data-testid="report-note-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReportDialog(false)}>Cancel</Button>
              <Button
                onClick={handleSendReport}
                disabled={reportBusy}
                className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1"
                data-testid="confirm-send-report-btn"
              >
                {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CaseKpi = ({ icon: Icon, label, value, sub, accent, pulse }) => (
  <div className="bg-white px-4 py-3 flex items-center gap-3">
    <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${accent ? 'bg-[#80C030]/10' : 'bg-[#0D5B69]/8'} ${pulse ? 'animate-pulse' : ''}`}>
      <Icon className={`w-4 h-4 ${accent ? 'text-[#5a8c20]' : 'text-[#0D5B69]'}`} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className={`text-sm font-bold truncate ${accent ? 'text-[#5a8c20]' : 'text-slate-800'}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 truncate">{sub}</p>}
    </div>
  </div>
);

const SLA_TONE = {
  ok:     { bg: 'bg-emerald-50', icon: 'text-emerald-600', text: 'text-emerald-700', bar: 'bg-emerald-500',  label: 'SLA' },
  amber:  { bg: 'bg-amber-50',   icon: 'text-amber-600',   text: 'text-amber-700',   bar: 'bg-amber-500',    label: 'SLA · 50%' },
  red:    { bg: 'bg-red-50',     icon: 'text-red-600',     text: 'text-red-700',     bar: 'bg-red-500',      label: 'SLA · 75%' },
  breach: { bg: 'bg-red-100',    icon: 'text-red-700',     text: 'text-red-800',     bar: 'bg-red-600',      label: 'SLA · BREACHED' },
};

const fmtSlaValue = (sla) => {
  if (sla.remainingMin >= 1) return `${Math.floor(sla.remainingMin)} min left`;
  if (sla.remainingMin >= 0) return '< 1 min left';
  const over = Math.floor(-sla.remainingMin);
  return over >= 1 ? `+${over} min over` : 'over now';
};

const SlaKpi = ({ sla, status }) => {
  // No SLA configured for this service
  if (!sla) {
    return (
      <div className="bg-white px-4 py-3 flex items-center gap-3" data-testid="sla-kpi-none">
        <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
          <Timer className="w-4 h-4 text-slate-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">SLA</p>
          <p className="text-sm font-bold truncate text-slate-400">No SLA set</p>
        </div>
      </div>
    );
  }
  // Closed/cancelled — display final outcome (met / breached / cancelled)
  if (status === 'closed') {
    const met = sla.pct < 100;
    return (
      <div className="bg-white px-4 py-3 flex items-center gap-3" data-testid="sla-kpi-closed">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${met ? 'bg-emerald-100' : 'bg-red-100'}`}>
          {met ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-red-600" />}
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">SLA · {sla.slaMin}m target</p>
          <p className={`text-sm font-bold truncate ${met ? 'text-emerald-700' : 'text-red-700'}`}>
            {met ? `Met (${Math.floor(sla.elapsedMin)}m)` : `Breached by ${Math.floor(sla.elapsedMin - sla.slaMin)}m`}
          </p>
        </div>
      </div>
    );
  }
  if (status === 'cancelled') {
    return (
      <div className="bg-white px-4 py-3 flex items-center gap-3" data-testid="sla-kpi-cancelled">
        <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
          <Timer className="w-4 h-4 text-slate-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">SLA · cancelled</p>
          <p className="text-sm font-bold truncate text-slate-500">N/A</p>
        </div>
      </div>
    );
  }
  // Live — show countdown + colour bar
  const tone = SLA_TONE[sla.tone];
  const barWidth = Math.min(100, Math.max(0, sla.pct));
  const pulseClass = sla.tone === 'breach' ? 'animate-pulse' : '';
  return (
    <div
      className={`relative px-4 py-3 flex items-center gap-3 ${tone.bg}`}
      data-testid={`sla-kpi-${sla.tone}`}
      title={`${sla.slaMin}-min response SLA · ${Math.floor(sla.pct)}% elapsed`}
    >
      <div className={`w-8 h-8 rounded-md ${tone.bg} flex items-center justify-center shrink-0 ${pulseClass} ring-1 ring-inset ring-current/10`}>
        <Timer className={`w-4 h-4 ${tone.icon}`} />
      </div>
      <div className="min-w-0">
        <p className={`text-[10px] uppercase tracking-wider font-semibold ${tone.text}`}>{tone.label}</p>
        <p className={`text-sm font-bold truncate ${tone.text}`}>{fmtSlaValue(sla)}</p>
      </div>
      {/* Bottom progress bar */}
      <div className="absolute left-0 right-0 bottom-0 h-1 bg-white/40">
        <div
          className={`h-full ${tone.bar} transition-all duration-700`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </div>
  );
};

const ACTIVITY_TONE = {
  open:        { bg: 'bg-red-50 border-red-200',         text: 'text-red-700',         dot: 'bg-red-500' },
  close:       { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700',     dot: 'bg-emerald-500' },
  cancel:      { bg: 'bg-slate-100 border-slate-200',    text: 'text-slate-600',       dot: 'bg-slate-500' },
  assign:      { bg: 'bg-blue-50 border-blue-200',       text: 'text-blue-700',        dot: 'bg-blue-500' },
  'assign-end':{ bg: 'bg-slate-50 border-slate-200',     text: 'text-slate-600',       dot: 'bg-slate-400' },
  note:        { bg: 'bg-amber-50 border-amber-200',     text: 'text-amber-800',       dot: 'bg-amber-500' },
  'note-edit': { bg: 'bg-amber-50 border-amber-200',     text: 'text-amber-700',       dot: 'bg-amber-400' },
  attach:      { bg: 'bg-violet-50 border-violet-200',   text: 'text-violet-700',      dot: 'bg-violet-500' },
};

const ActivityChip = ({ entry }) => {
  const tone = ACTIVITY_TONE[entry.tone] || ACTIVITY_TONE.note;
  const Icon = entry.icon;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${tone.bg} ${tone.text} shrink-0 max-w-[280px]`}
      title={`${entry.label} · ${new Date(entry.at).toLocaleString('en-ZA')}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot} shrink-0`} />
      <Icon className="w-3 h-3 shrink-0" />
      <span className="truncate">{entry.label}</span>
      <span className="opacity-60 shrink-0">· {fmtRelative(entry.at)}</span>
    </div>
  );
};

const Field = ({ label, children, className = '' }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{label}</Label>
    {children}
  </div>
);
